import { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Trash2,
  Edit2,
  Calendar,
  Ticket,
  Percent,
  TrendingDown,
  Info,
  X,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { useToast } from "@/components/admin/ui/Toast";

interface Coupon {
  id?: number;
  title: string;
  code: string;
  method: "code" | "automatic";
  discount_type: "Percent" | "Fixed";
  amount: number;
  applies_to: string;
  limit_total: number | null;
  limit_user: number | null;
  min_purchase: number;
  start_date: string;
  end_date: string;
  status?: string;
}

export function Coupons() {
  const [activeTab, setActiveTab] = useState<"coupons" | "referrals">("coupons");
  const [couponsList, setCouponsList] = useState<Coupon[]>([]);
  const [referralsList, setReferralsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { showToast } = useToast();

  // Form / Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);

  // Form fields
  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const [method, setMethod] = useState<"code" | "automatic">("code");
  const [discountType, setDiscountType] = useState<"Percent" | "Fixed">("Percent");
  const [amount, setAmount] = useState<number>(0);
  const [appliesTo, setAppliesTo] = useState("All courses");
  const [limitTotalEnabled, setLimitTotalEnabled] = useState(false);
  const [limitTotal, setLimitTotal] = useState<number | "">("");
  const [limitUserEnabled, setLimitUserEnabled] = useState(false);
  const [limitUser, setLimitUser] = useState<number | "">("");
  const [minPurchaseType, setMinPurchaseType] = useState<"none" | "amount" | "quantity">("none");
  const [minPurchaseAmount, setMinPurchaseAmount] = useState<number>(0);
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDateEnabled, setEndDateEnabled] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [availableCourses, setAvailableCourses] = useState<any[]>([]);

  const fetchCoupons = () => {
    setLoading(true);
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) {
      // Offline fallback
      setCouponsList([
        {
          id: 1,
          title: "সামার সেল ২০২৬",
          code: "SUMMER20",
          method: "code",
          discount_type: "Percent",
          amount: 20,
          applies_to: "All courses",
          limit_total: 100,
          limit_user: 1,
          min_purchase: 0,
          start_date: "2026-06-01",
          end_date: "2026-08-31",
          status: "Active",
        },
        {
          id: 2,
          title: "ঈদ অফার ২০২৬",
          code: "EID500",
          method: "code",
          discount_type: "Fixed",
          amount: 500,
          applies_to: "All courses",
          limit_total: 50,
          limit_user: 1,
          min_purchase: 1500,
          start_date: "2026-06-15",
          end_date: "2026-06-30",
          status: "Active",
        }
      ]);
      setLoading(false);
      return;
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_admin_coupons",
        nonce: adminNonce,
      },
      success: (res: any) => {
        setLoading(false);
        if (res.success && Array.isArray(res.data)) {
          setCouponsList(res.data);
        } else {
          showToast("Failed to parse coupons.", "error");
        }
      },
      error: (err: any) => {
        setLoading(false);
        showToast("Error fetching coupons.", "error");
        console.error("AJAX Error:", err);
      },
    });
  };

  const fetchReferrals = () => {
    setLoading(true);
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).hubInstructor?.nonce ||
      (window as any).chorchaAdmin?.nonce;

    if (!ajaxUrl) {
      setLoading(false);
      return;
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_admin_referrals",
        nonce: adminNonce,
      },
      success: (res: any) => {
        setLoading(false);
        if (res.success && Array.isArray(res.data)) {
          setReferralsList(res.data);
        } else {
          showToast("Failed to parse referrals.", "error");
        }
      },
      error: (err: any) => {
        setLoading(false);
        showToast("Error fetching referrals.", "error");
        console.error("AJAX Error:", err);
      },
    });
  };

  useEffect(() => {
    if (activeTab === "referrals") {
      fetchReferrals();
    } else {
      fetchCoupons();
    }
  }, [activeTab]);

  useEffect(() => {
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (ajaxUrl && (window as any).jQuery) {
      (window as any).jQuery.ajax({
        url: ajaxUrl,
        method: "POST",
        data: {
          action: "chorcha_get_admin_courses",
          nonce: adminNonce,
        },
        success: (res: any) => {
          if (res.success && Array.isArray(res.data)) {
            setAvailableCourses(res.data);
          }
        },
      });
    }
  }, []);

  const generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let generated = "";
    for (let i = 0; i < 8; i++) {
      generated += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCode(generated);
  };

  const handleOpenModal = (coupon: Coupon | null = null) => {
    setEditingCoupon(coupon);
    if (coupon) {
      setTitle(coupon.title);
      setCode(coupon.code);
      setMethod(coupon.method);
      setDiscountType(coupon.discount_type);
      setAmount(coupon.amount);
      setAppliesTo(coupon.applies_to);
      setLimitTotalEnabled(coupon.limit_total !== null);
      setLimitTotal(coupon.limit_total ?? "");
      setLimitUserEnabled(coupon.limit_user !== null);
      setLimitUser(coupon.limit_user ?? "");
      setMinPurchaseType(coupon.min_purchase > 0 ? "amount" : "none");
      setMinPurchaseAmount(coupon.min_purchase);
      setStartDate(coupon.start_date);
      setEndDateEnabled(!!coupon.end_date);
      setEndDate(coupon.end_date || "");
    } else {
      setTitle("");
      setCode("");
      setMethod("code");
      setDiscountType("Percent");
      setAmount(0);
      setAppliesTo("All courses");
      setLimitTotalEnabled(false);
      setLimitTotal("");
      setLimitUserEnabled(false);
      setLimitUser("");
      setMinPurchaseType("none");
      setMinPurchaseAmount(0);
      setStartDate(new Date().toISOString().split("T")[0]);
      setEndDateEnabled(false);
      setEndDate("");
    }
    setIsModalOpen(true);
  };

  const handleSaveCoupon = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !code.trim() || amount <= 0) {
      showToast("Please fill in the title, code, and discount amount.", "error");
      return;
    }

    const couponData: Coupon = {
      id: editingCoupon?.id,
      title: title.trim(),
      code: code.trim().toUpperCase(),
      method,
      discount_type: discountType,
      amount,
      applies_to: appliesTo,
      limit_total: limitTotalEnabled && limitTotal !== "" ? Number(limitTotal) : null,
      limit_user: limitUserEnabled && limitUser !== "" ? Number(limitUser) : null,
      min_purchase: minPurchaseType === "amount" ? minPurchaseAmount : 0,
      start_date: startDate,
      end_date: endDateEnabled ? endDate : "",
    };

    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) {
      // Offline fallback
      if (editingCoupon) {
        setCouponsList((prev) =>
          prev.map((c) => (c.id === editingCoupon.id ? { ...couponData, status: "Active" } : c))
        );
        showToast("Coupon updated locally!", "success");
      } else {
        setCouponsList((prev) => [...prev, { ...couponData, id: Date.now(), status: "Active" }]);
        showToast("Coupon created locally!", "success");
      }
      setIsModalOpen(false);
      return;
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_save_admin_coupon",
        nonce: adminNonce,
        coupon: couponData,
      },
      success: (res: any) => {
        if (res.success) {
          showToast(res.data.message || "Coupon saved successfully!", "success");
          fetchCoupons();
          setIsModalOpen(false);
        } else {
          showToast(res.data || "Failed to save coupon.", "error");
        }
      },
      error: () => {
        showToast("Error processing request.", "error");
      },
    });
  };

  const handleDeleteCoupon = (id: number) => {
    if (!confirm("Are you sure you want to delete this coupon?")) return;

    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) {
      // Offline fallback
      setCouponsList((prev) => prev.filter((c) => c.id !== id));
      showToast("Coupon deleted locally!", "success");
      return;
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_delete_admin_coupon",
        nonce: adminNonce,
        id,
      },
      success: (res: any) => {
        if (res.success) {
          showToast(res.data.message || "Coupon deleted successfully!", "success");
          fetchCoupons();
        } else {
          showToast(res.data || "Failed to delete coupon.", "error");
        }
      },
      error: () => {
        showToast("Error deleting coupon.", "error");
      },
    });
  };

  const filteredCoupons = couponsList.filter((c) => {
    const q = search.toLowerCase();
    return c.code.toLowerCase().includes(q) || c.title.toLowerCase().includes(q);
  });

  const filteredReferrals = referralsList.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.referrer_name && r.referrer_name.toLowerCase().includes(q)) ||
      (r.referee_name && r.referee_name.toLowerCase().includes(q)) ||
      (r.referral_code && r.referral_code.toLowerCase().includes(q)) ||
      (r.referrer_mobile && r.referrer_mobile.toLowerCase().includes(q))
    );
  });

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {activeTab === "coupons" ? "Coupons" : "Referral Tracking"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeTab === "coupons"
              ? "Manage promotional discount codes and campaigns"
              : "Monitor student referral performance, leads, and commission splits"}
          </p>
        </div>
        {activeTab === "coupons" && (
          <Button
            onClick={() => handleOpenModal(null)}
            leftIcon={<Plus className="w-4.5 h-4.5" />}
            className="bg-[#00C9A7] hover:bg-[#00b898] text-white shadow-lg shadow-[#00C9A7]/15 self-start sm:self-center"
          >
            Add New Coupon
          </Button>
        )}
      </div>

      {/* Sub-tabs Navigation */}
      <div className="flex border-b border-gray-100 gap-6">
        <button
          onClick={() => {
            setActiveTab("coupons");
            setSearch("");
          }}
          className={`pb-3 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
            activeTab === "coupons"
              ? "border-[#00C9A7] text-[#00C9A7]"
              : "border-transparent text-gray-400 hover:text-gray-600"
          }`}
        >
          Promo Coupons
        </button>
        <button
          onClick={() => {
            setActiveTab("referrals");
            setSearch("");
          }}
          className={`pb-3 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
            activeTab === "referrals"
              ? "border-[#00C9A7] text-[#00C9A7]"
              : "border-transparent text-gray-400 hover:text-gray-600"
          }`}
        >
          Referral Program History
        </button>
      </div>

      {/* Table/View Wrapper */}
      <div className="bg-white rounded-3xl border border-gray-150 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-4.5 w-4.5" />
            <Input
              type="text"
              placeholder={
                activeTab === "coupons"
                  ? "Search coupons by code or title..."
                  : "Search by referrer, referee, or code..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 text-sm border-gray-200"
            />
          </div>
        </div>

        {/* Content List */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-4 border-[#00C9A7]/20 border-t-[#00C9A7] rounded-full animate-spin" />
              <span className="text-xs text-gray-400 font-semibold font-bangla">
                {activeTab === "coupons" ? "কুপন তালিকা লোড হচ্ছে..." : "রেফারেল রেকর্ড লোড হচ্ছে..."}
              </span>
            </div>
          ) : activeTab === "coupons" ? (
            filteredCoupons.length === 0 ? (
              <div className="text-center py-20 space-y-3">
                <div className="text-4xl">🎫</div>
                <h3 className="font-bangla text-base font-bold text-gray-700">কোনো কুপন কোড পাওয়া যায়নি!</h3>
                <p className="text-xs text-gray-400">নতুন কুপন কোড যুক্ত করতে উপরে "Add New Coupon" বাটনে ক্লিক করুন</p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider pl-6">Coupon Code</th>
                    <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Campaign Name</th>
                    <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Discount</th>
                    <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Applies To</th>
                    <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Total Limit</th>
                    <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Usage Limit/User</th>
                    <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Validity Period</th>
                    <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCoupons.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="p-4 pl-6">
                        <span className="font-mono bg-purple-50 text-purple-700 border border-purple-100 px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider">
                          {coupon.code}
                        </span>
                      </td>
                      <td className="p-4 font-bold text-gray-800 text-sm">{coupon.title}</td>
                      <td className="p-4">
                        <span className="font-extrabold text-gray-900 text-sm">
                          {coupon.discount_type === "Percent" ? `${coupon.amount}%` : `৳${coupon.amount}`}
                        </span>
                        <span className="text-[10px] text-gray-400 block font-semibold">
                          {coupon.discount_type === "Percent" ? "Percent Off" : "Fixed Discount"}
                        </span>
                      </td>
                      <td className="p-4 text-xs font-semibold text-gray-500">{coupon.applies_to}</td>
                      <td className="p-4 text-xs font-bold text-gray-700">
                        {coupon.limit_total !== null ? coupon.limit_total : "Unlimited"}
                      </td>
                      <td className="p-4 text-xs font-bold text-gray-700">
                        {coupon.limit_user !== null ? `${coupon.limit_user} time(s)` : "Unlimited"}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col text-left">
                          <span className="text-[10px] font-bold text-gray-600">Start: {coupon.start_date}</span>
                          {coupon.end_date ? (
                            <span className="text-[10px] font-semibold text-rose-500">End: {coupon.end_date}</span>
                          ) : (
                            <span className="text-[9px] text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded self-start font-bold mt-0.5">Indefinite</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right pr-6 space-x-1">
                        <button
                          onClick={() => handleOpenModal(coupon)}
                          className="p-1.5 hover:bg-gray-150 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer transition-colors"
                          title="Edit Coupon"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => coupon.id && handleDeleteCoupon(coupon.id)}
                          className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-rose-600 rounded-lg cursor-pointer transition-colors"
                          title="Delete Coupon"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : filteredReferrals.length === 0 ? (
            <div className="text-center py-20 space-y-3">
              <div className="text-4xl">👥</div>
              <h3 className="font-bangla text-base font-bold text-gray-700">কোনো রেফারেল রেকর্ড পাওয়া যায়নি!</h3>
              <p className="text-xs text-gray-400">শিক্ষার্থীরা যখন তাদের রেফারেল কোড শেয়ার করবে এবং লিড বা সেল তৈরি হবে, তা এখানে প্রদর্শিত হবে।</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider pl-6">Referrer</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Referral Code</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Referee (Invited User)</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Type</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Purchase Amount</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Referrer Earning</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Date</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider pr-6">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredReferrals.map((ref, idx) => (
                  <tr key={ref.id + "-" + idx} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4 pl-6">
                      <div className="flex flex-col text-left">
                        <span className="font-bold text-gray-800 text-sm">{ref.referrer_name}</span>
                        <span className="text-[10px] text-gray-400 font-semibold">{ref.referrer_mobile || "No phone"}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-mono bg-purple-50 text-purple-700 border border-purple-100 px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider">
                        {ref.referral_code || "N/A"}
                      </span>
                    </td>
                    <td className="p-4 font-bold text-gray-800 text-sm">{ref.referee_name}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                        ref.type === "Commission"
                          ? "bg-indigo-50 text-indigo-600 border border-indigo-100"
                          : "bg-blue-50 text-blue-600 border border-blue-100"
                      }`}>
                        {ref.type}
                      </span>
                    </td>
                    <td className="p-4 font-bold text-gray-700 text-xs">
                      {ref.amount > 0 ? `৳${ref.amount}` : "—"}
                    </td>
                    <td className="p-4">
                      {ref.commission > 0 ? (
                        <span className="font-extrabold text-emerald-600 text-sm">
                          ৳{ref.commission}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-4 text-xs font-semibold text-gray-500">{ref.date}</td>
                    <td className="p-4 pr-6">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                        ref.status === "completed" || ref.status === "approved"
                          ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                          : "bg-amber-50 text-amber-600 border border-amber-100"
                      }`}>
                        {ref.status === "completed" || ref.status === "approved" ? "Completed" : "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create / Edit Coupon Fullscreen Modal (matching mockup layout) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-start justify-center p-4 md:p-6 backdrop-blur-sm overflow-y-auto pt-16">
          <div className="bg-white rounded-3xl max-w-5xl w-full p-6 md:p-8 relative shadow-2xl border border-gray-150 grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8 text-left">
            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer z-10"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Left Form side (2/3 cols) */}
            <form onSubmit={handleSaveCoupon} className="lg:col-span-2 space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  {editingCoupon ? "Edit Coupon" : "Create Coupon"}
                </h3>
                <p className="text-xs text-gray-400 mt-1">
                  Create custom discount codes and validities for your courses.
                </p>
              </div>

              {/* Coupon Info */}
              <div className="space-y-4 bg-gray-50/50 border border-gray-100 rounded-2xl p-5">
                <h4 className="text-xs font-extrabold uppercase text-gray-400 tracking-wider flex items-center gap-1.5">
                  <Ticket className="w-4 h-4 text-purple-500" />
                  Coupon Info
                </h4>

                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-xs font-bold text-gray-700">
                    <span className="text-gray-400 font-medium">Method:</span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        checked={method === "code"}
                        onChange={() => setMethod("code")}
                        className="accent-purple-500"
                      />
                      Code
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        checked={method === "automatic"}
                        onChange={() => setMethod("automatic")}
                        className="accent-purple-500"
                      />
                      Automatic
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Title / Name</label>
                      <Input
                        type="text"
                        placeholder="e.g. Summer Sale 2026"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="text-xs border-gray-200 focus:border-purple-500"
                        required
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Coupon Code</label>
                        {method === "code" && (
                          <button
                            type="button"
                            onClick={generateCode}
                            className="text-[10px] text-purple-600 hover:text-purple-800 font-bold hover:underline"
                          >
                            Generate Code
                          </button>
                        )}
                      </div>
                      <Input
                        type="text"
                        placeholder="e.g. SUMMER20"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="text-xs border-gray-200 focus:border-purple-500 font-mono tracking-wider"
                        disabled={method === "automatic"}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Discount Section */}
              <div className="space-y-4 bg-gray-50/50 border border-gray-100 rounded-2xl p-5">
                <h4 className="text-xs font-extrabold uppercase text-gray-400 tracking-wider flex items-center gap-1.5">
                  <Percent className="w-4 h-4 text-emerald-500" />
                  Discount Details
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Discount Type</label>
                    <select
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value as "Percent" | "Fixed")}
                      className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-purple-500"
                    >
                      <option value="Percent">Percent (%)</option>
                      <option value="Fixed">Fixed Amount (৳)</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Discount Value</label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      className="text-xs border-gray-200 focus:border-purple-500"
                      min="1"
                      required
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2 space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Applies to</label>
                    <select
                      value={appliesTo.startsWith("Course:") ? "Specific course" : appliesTo}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "Specific course") {
                          if (availableCourses.length > 0) {
                            setAppliesTo(`Course: ${availableCourses[0].title}`);
                          } else {
                            setAppliesTo("Course: ");
                          }
                        } else {
                          setAppliesTo(val);
                        }
                      }}
                      className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-purple-500"
                    >
                      <option value="All courses">All courses</option>
                      <option value="Specific course">Specific course</option>
                      <option value="Specific batches">Specific batches</option>
                    </select>

                    {appliesTo.startsWith("Course:") && (
                      <select
                        value={appliesTo.replace("Course: ", "")}
                        onChange={(e) => setAppliesTo(`Course: ${e.target.value}`)}
                        className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-purple-500 mt-2"
                      >
                        {availableCourses.map((c) => (
                          <option key={c.id} value={c.title}>
                            {c.title}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* Usage Limitation & Validity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Limitations */}
                <div className="space-y-4 bg-gray-50/50 border border-gray-100 rounded-2xl p-5">
                  <h4 className="text-xs font-extrabold uppercase text-gray-400 tracking-wider flex items-center gap-1.5">
                    <TrendingDown className="w-4 h-4 text-blue-500" />
                    Limitations
                  </h4>

                  <div className="space-y-3 text-xs">
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={limitTotalEnabled}
                          onChange={(e) => setLimitTotalEnabled(e.target.checked)}
                          className="accent-purple-500"
                        />
                        Limit coupon usages in total
                      </label>
                      {limitTotalEnabled && (
                        <Input
                          type="number"
                          placeholder="e.g. 100"
                          value={limitTotal}
                          onChange={(e) => setLimitTotal(e.target.value === "" ? "" : Number(e.target.value))}
                          className="text-xs py-1.5 h-auto border-gray-200"
                          min="1"
                        />
                      )}
                    </div>

                    <div className="space-y-2 pt-2 border-t border-gray-100">
                      <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={limitUserEnabled}
                          onChange={(e) => setLimitUserEnabled(e.target.checked)}
                          className="accent-purple-500"
                        />
                        Limit usages per customer
                      </label>
                      {limitUserEnabled && (
                        <Input
                          type="number"
                          placeholder="e.g. 1"
                          value={limitUser}
                          onChange={(e) => setLimitUser(e.target.value === "" ? "" : Number(e.target.value))}
                          className="text-xs py-1.5 h-auto border-gray-200"
                          min="1"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Validity */}
                <div className="space-y-4 bg-gray-50/50 border border-gray-100 rounded-2xl p-5">
                  <h4 className="text-xs font-extrabold uppercase text-gray-400 tracking-wider flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-rose-500" />
                    Validity Period
                  </h4>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Starts From</label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="text-xs py-1.5 h-auto border-gray-200"
                        required
                      />
                    </div>

                    <div className="space-y-2 pt-2 border-t border-gray-100">
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={endDateEnabled}
                          onChange={(e) => setEndDateEnabled(e.target.checked)}
                          className="accent-purple-500"
                        />
                        Set expiration date
                      </label>
                      {endDateEnabled && (
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="text-xs py-1.5 h-auto border-gray-200"
                          required
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Min Purchase */}
              <div className="space-y-4 bg-gray-50/50 border border-gray-100 rounded-2xl p-5">
                <h4 className="text-xs font-extrabold uppercase text-gray-400 tracking-wider flex items-center gap-1.5">
                  <Info className="w-4 h-4 text-indigo-500" />
                  Minimum Purchase Requirements
                </h4>

                <div className="space-y-3">
                  <div className="flex flex-wrap gap-4 text-xs font-bold text-gray-700">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        checked={minPurchaseType === "none"}
                        onChange={() => setMinPurchaseType("none")}
                        className="accent-purple-500"
                      />
                      No minimum requirements
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        checked={minPurchaseType === "amount"}
                        onChange={() => setMinPurchaseType("amount")}
                        className="accent-purple-500"
                      />
                      Minimum purchase amount (৳)
                    </label>
                  </div>

                  {minPurchaseType === "amount" && (
                    <div className="space-y-1 pt-2">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Minimum Amount (৳)</label>
                      <Input
                        type="number"
                        placeholder="e.g. 1500"
                        value={minPurchaseAmount}
                        onChange={(e) => setMinPurchaseAmount(Number(e.target.value))}
                        className="text-xs border-gray-200 focus:border-purple-500"
                        min="1"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="flex-1 bg-[#00C9A7] hover:bg-[#00b898] text-white py-3 rounded-xl shadow-lg shadow-[#00C9A7]/10"
                >
                  Save Coupon Details
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 border-gray-200 text-gray-500"
                >
                  Cancel
                </Button>
              </div>
            </form>

            {/* Right Live Preview Panel (1/3 col) */}
            <div className="lg:border-l lg:border-gray-100 lg:pl-8 flex flex-col justify-start space-y-6 pt-6 lg:pt-0">
              <div>
                <h4 className="text-sm font-bold text-gray-800">Coupon Live Preview</h4>
                <p className="text-[10px] text-gray-400 mt-0.5">Real-time view of how the coupon will render for students</p>
              </div>

              {/* Ticket Card Component */}
              <div className="relative bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-3xl p-6 overflow-hidden shadow-xl border border-purple-500/20 aspect-[1.6/1] flex flex-col justify-between group">
                {/* Dashed edge separators */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-8 bg-white rounded-r-full" />
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-8 bg-white rounded-l-full" />
                <div className="absolute left-8 right-8 top-1/2 border-t border-dashed border-white/20 -translate-y-1/2 pointer-events-none" />

                {/* Top Half */}
                <div className="space-y-1 text-left">
                  <div className="flex justify-between items-center">
                    <span className="text-[9px] bg-white/20 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">
                      {discountType === "Percent" ? "Percent Off" : "Fixed Cash"}
                    </span>
                    <span className="text-[8px] text-white/60 font-medium">SHUDDHOSHIKHA</span>
                  </div>
                  <h4 className="text-2xl font-extrabold tracking-tight mt-1">
                    {discountType === "Percent" ? `${amount || 0}%` : `৳${amount || 0}`} OFF
                  </h4>
                  <p className="text-[10px] text-white/80 font-bangla line-clamp-1">
                    {title || "ক্যাম্পেইন শিরোনাম"}
                  </p>
                </div>

                {/* Bottom Half */}
                <div className="flex justify-between items-end mt-4">
                  <div className="text-left space-y-0.5">
                    <span className="text-[8px] uppercase tracking-wider text-white/50 block">PROMO CODE</span>
                    <span className="font-mono text-sm font-extrabold tracking-widest text-yellow-300">
                      {code ? code.toUpperCase() : "PROMOCODE"}
                    </span>
                  </div>
                  <div className="text-right space-y-0.5">
                    <span className="text-[8px] uppercase tracking-wider text-white/50 block">EXPIRATION</span>
                    <span className="text-[9px] font-bold">
                      {endDateEnabled && endDate ? endDate : "Never Expires"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Explainer / Tips */}
              <div className="bg-purple-50/50 rounded-2xl p-4 border border-purple-100/50 space-y-2 text-xs text-purple-950/80">
                <h5 className="font-bold flex items-center gap-1.5 text-purple-900 text-xs">
                  <HelpCircle className="w-4.5 h-4.5 text-purple-600" />
                  Coupon Rules Setup
                </h5>
                <ul className="list-disc pl-4 space-y-1 text-[11px] leading-relaxed">
                  <li>Automatic method applies discount directly on checkout without inputting a code.</li>
                  <li>Percent coupons subtract a percentage (e.g. 20%) from the course cart.</li>
                  <li>Usage limit helps run flash sales (e.g. first 50 students only).</li>
                  <li>Set start and expiration dates to schedule campaigns ahead of time.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
